import React, { FC, useState } from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { Button, Menu, MenuButton, MenuList, MenuItem, MenuProps, useTheme, Portal } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { rgba } from 'polished';
import { rem } from 'utils/fonts';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import { useSelector } from 'react-redux';

interface CustomDropdownValue {
  id: string;
  label: string;
}

const StyledMenuItem = styled(MenuItem)`
  height: 50px;
  position: relative;
  font-size: ${rem(15)};
  font-weight: 600;
  padding: 0 20px;
`;

const StyledMenuButton = styled(MenuButton)<{ hideLabel?: boolean }>`
  min-width: auto;
  line-height: 23px;
  border-radius: 35px;
  justify-content: flex-start;
  padding: ${(props): string => (props.hideLabel ? '0 14px' : '0 20px')};

  > span {
    margin: 0;
  }

  svg {
    font-size: ${rem(22)};
    margin-left: ${(props): string => (props.hideLabel ? '0px' : '4px')};
    margin-right: ${(props): string => (props.hideLabel ? '0px' : '-4px')};
  }
`;

const StyledMenuList = styled(MenuList)`
  border-radius: 8px;
  padding: 5px 0;
  overflow: hidden;
  min-width: 154px;
`;

const Overlay = styled.div`
  content: '';
  cursor: pointer;
  position: absolute;
  z-index: 999;
  background: transparent;
  height: 40px;
  left: 0;
  top: 0;
  width: 100%;
`;

interface Props {
  items: CustomDropdownValue[];
  selectedValue?: CustomDropdownValue;
  hideLabel?: boolean;
  onSelect?: (id: string, value: string) => void;
}

const CustomDropdown: FC<Props & Partial<MenuProps>> = ({ items, onSelect, selectedValue, hideLabel = false, ...props }) => {
  const { colors } = useTheme();
  const [currentItem, setCurrentItem] = useState<CustomDropdownValue>(selectedValue ? selectedValue : items[0]);
  const isCorporateTheme = useSelector(isCorporateThemeSelector);

  const onMenuItemClick = (item: CustomDropdownValue): void => {
    setCurrentItem(item);
    onSelect && onSelect(item.id, item.label);
  };

  const preventFocus = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    if (document.activeElement) {
      (document.activeElement as HTMLDivElement).blur();
    }
  };

  return (
    <Menu {...props} placement="bottom-end">
      {({ isOpen }): React.ReactNode => (
        <>
          <StyledMenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            hideLabel={hideLabel}
            fontFamily="body"
            sx={{
              fontSize: isCorporateTheme ? rem(12) : rem(14),
              fontWeight: isCorporateTheme ? '500' : '600',
              height: isCorporateTheme ? '30px' : '38px',
              border: isCorporateTheme ? 'none' : `1px solid ${rgba(colors.gray[900], 0.1)}`,
              color: 'gray.900',
              bg: isCorporateTheme ? 'gray.100' : 'white',
              '& svg': { fill: 'white' },
              '&:hover': { bg: isCorporateTheme ? 'gray.200' : 'white', borderColor: 'primary.500' },
              '&[data-active]': {
                background: 'secondary.300',
              },
            }}
          >
            {!hideLabel ? currentItem.label : ''}
          </StyledMenuButton>
          <Portal>
            <StyledMenuList
              bg="white"
              boxShadow={`0 14.2045px 28.4091px ${rgba(colors.gray[700], 0.08)} !important`}
              border={`1px solid ${rgba(colors.gray[200], 0.5)}`}
            >
              {items.map((item: CustomDropdownValue) => (
                <StyledMenuItem key={item.id} fontFamily="body" onClick={(): void => onMenuItemClick(item)}>
                  {item.label}
                </StyledMenuItem>
              ))}
            </StyledMenuList>
          </Portal>
          {isOpen && <Overlay onClick={preventFocus} />}
        </>
      )}
    </Menu>
  );
};

export default CustomDropdown;
